import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BsSearch } from 'react-icons/bs';
import useTokenStore from '../../stores/tokens';
import { compatApi } from '../../utils/Intercepter';
import { toast } from 'react-toastify';
import { useDaumPostcodePopup } from 'react-daum-postcode';
import {
  isImageFile,
  loadMultipleImagePreviews,
  revokeMultipleImageUrls,
  revokeImageUrl,
  downloadFile
} from '../../utils/ImageUtil';
import { PURPLE, MINT, hexToRgba } from "../../utils/designUtils";

export default function ProjectCreate() {
  const { user } = useTokenStore();
  const navigate = useNavigate();
  const location = useLocation();
  const userEmail = useTokenStore((state) => state.user?.userEmail);
  const currentOrganizationCode = useTokenStore((state) => state.user?.currentOrganizationCode);
  const currentRole = useTokenStore((state) => state.user?.currentRole);
  const isEditMode = location.pathname === '/project/edit';
  const existingProject = location.state;
  const isMaster = currentRole === 'MASTER';

  // 프로젝트 기본 정보
  const [projectData, setProjectData] = useState({
    projectName: '',
    client: '',
    contract: '',
    contractualStartDate: '',
    contractualEndDate: '',
    startDate: '',
    endDate: '',
    contractedDate: '',
    address: '',
    addressDetail: '',
    manager: '',
    managerEmail: '',
    managerPhone: '',
    content: '',
    memo: '',
    taxInvoiceType: 'none',
    taxInvoiceDay: '',
    taxInvoiceWeek: '',
    taxInvoiceWeekday: '',
    projectType: 'GENERAL',
  });

  // 프로젝트 인원 정보
  const [personnel, setPersonnel] = useState([]);

  // 계약 연장 이력
  const [contractExtensions, setContractExtensions] = useState([]);

  // 파일 첨부
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [filePreviewUrls, setFilePreviewUrls] = useState({});

  useEffect(() => {
    if (isEditMode && existingProject?.projectId) {
      loadProjectData(existingProject.projectId);
    }
  }, [isEditMode, existingProject]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentOrganizationCode) {
      loadWorkmanList();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProjectData = async (projectId) => {
    try {
      const response = await compatApi.post('/project/projectInfo', {
        projectId: projectId
      });

      if (response.ok && response.data) {
        const project = response.data;
        setProjectData({
          projectId: project.projectId || '',
          projectName: project.projectName || '',
          client: project.client || '',
          contract: project.contract || '',
          contractualStartDate: project.contractualStartDate || '',
          contractualEndDate: project.contractualEndDate || '',
          startDate: project.startDate || '',
          endDate: project.endDate || '',
          contractedDate: project.contractedDate || '',
          address: project.address || '',
          addressDetail: project.addressDetail || '',
          manager: project.manager || '',
          managerEmail: project.managerEmail || '',
          managerPhone: project.managerPhone || '',
          content: project.content || '',
          memo: project.memo || '',
          taxInvoiceType: project.taxInvoiceType || 'none',
          taxInvoiceDay: project.taxInvoiceDay || '',
          taxInvoiceWeek: project.taxInvoiceWeek || '',
          taxInvoiceWeekday: project.taxInvoiceWeekday || '',
          projectType: project.projectType || 'GENERAL',
        });

        if (project.personnelList) {
          const mappedPersonnel = project.personnelList.map(p => ({
            ...p,
            plannedStartDate: p.startDate,
            plannedEndDate: p.endDate,
          }));
          setPersonnel(mappedPersonnel);
        }

        if (project.fileList) {
          setAttachedFiles(project.fileList);
          loadImagePreviews(project.fileList);
        }

        setContractExtensions((project.contractExtensions || []).map(e => ({
          extensionStartDate: e.extensionStartDate || '',
          extensionEndDate: e.extensionEndDate || '',
        })));
      } else {
        toast.error('프로젝트 정보를 불러오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('프로젝트 정보 로드 오류:', error);
      toast.error('프로젝트 정보를 불러오는 중 오류가 발생했습니다.');
    }
  };

  const loadImagePreviews = async (files) => {
    const urls = await loadMultipleImagePreviews(files);
    setFilePreviewUrls(prev => ({ ...prev, ...urls }));
  };

  useEffect(() => {
    return () => {
      revokeMultipleImageUrls(filePreviewUrls);
    };
  }, [filePreviewUrls]);

  const [showPersonnelModal, setShowPersonnelModal] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [allWorkmanList, setAllWorkmanList] = useState([]);
  const [selectedPersonnel, setSelectedPersonnel] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
    actualStartDate: '',
    actualEndDate: ''
  });

  const [showTechStackModal, setShowTechStackModal] = useState(false);
  const [techStackIndex, setTechStackIndex] = useState(null);
  const [techStackForm, setTechStackForm] = useState({ os: '', dbInfo: '', language: '' });

  const popupOpen = useDaumPostcodePopup();
  const handleSearchAddress = () => {
    popupOpen({ onComplete: handleComplete });
  };
  const handleComplete = (data) => {
    let fullAddress = data.address;
    let extraAddress = '';

    if (data.addressType === 'R') {
      if (data.bname !== '') extraAddress += data.bname;
      if (data.buildingName !== '') extraAddress += (extraAddress !== '' ? `, ${data.buildingName}` : data.buildingName);
      if (extraAddress !== '') fullAddress += ` (${extraAddress})`;
    }
    setProjectData((prev) => ({ ...prev, address: fullAddress }));
  };

  const validateDateRange = (startDate, endDate, startName, endName) => {
    if (startDate && endDate && startDate > endDate) {
      toast.error(`${endName}은(는) ${startName}보다 빠를 수 없습니다.`);
      return false;
    }
    return true;
  };

  const handleProjectInfoChange = (e) => {
    const { name, value } = e.target;
    if (name === 'managerPhone' && !/^[0-9-]*$/.test(value)) return;
    if (['content', 'projectName', 'client'].includes(name) && value.length > 100) return; // wit_project varchar(100)
    const newProjectData = { ...projectData, [name]: value };

    if (name === 'contractualStartDate') {
      newProjectData.startDate = value;
      newProjectData.contractedDate = value;
    }

    if (name === 'contractualStartDate' || name === 'contractualEndDate') {
      if (!validateDateRange(newProjectData.contractualStartDate, newProjectData.contractualEndDate, '계약 시작일', '계약 종료일')) return;
    }
    if (name === 'startDate' || name === 'endDate') {
      if (!validateDateRange(newProjectData.startDate, newProjectData.endDate, '실제 시작일', '실제 종료일')) return;
    }

    setProjectData(newProjectData);
  };

  const loadWorkmanList = async () => {
    try {
      const response = await compatApi.post('/project/findWorkmanList', {
        user: user || '',
        organizationCode: currentOrganizationCode
      });

      if (response.ok) {
        const sortedList = (response.data || []).sort((a, b) => (a.userName || '').localeCompare(b.userName || '', 'ko'));
        setAllWorkmanList(sortedList || []);
      } else {
        toast.error('인원 목록을 불러오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('인원 목록 로드 오류:', error);
      toast.error('인원 목록을 불러오는 중 오류가 발생했습니다.');
    }
  };

  const getFilteredWorkmanList = () => {
    const registeredEmails = personnel.map(p => p.userEmail);
    const availableList = allWorkmanList.filter(person => !registeredEmails.includes(person.userEmail));
    if (!searchKeyword.trim()) return availableList;
    return availableList.filter(person =>
      person.userName?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
      person.userEmail?.toLowerCase().includes(searchKeyword.toLowerCase())
    );
  };

  const handleTogglePersonnel = (person) => {
    setSelectedPersonnel(prev => {
      if (prev.find(p => p.userEmail === person.userEmail)) return prev.filter(p => p.userEmail !== person.userEmail);
      return [...prev, person];
    });
  };

  const handleNextStep = () => {
    setDateRange({
      startDate: projectData.contractualStartDate || '',
      endDate: projectData.contractualEndDate || '',
      actualStartDate: projectData.startDate || '',
      actualEndDate: projectData.endDate || ''
    });
    setModalStep(2);
  };

  const handlePreviousStep = () => setModalStep(1);

  const handleAddPersonnelComplete = () => {
    if (!validateDateRange(dateRange.startDate, dateRange.endDate, '계획 시작일', '계획 종료일')) return;
    if (!validateDateRange(dateRange.actualStartDate, dateRange.actualEndDate, '실제 시작일', '실제 종료일')) return;

    const newPersonnel = selectedPersonnel.map(person => ({
      userEmail: person.userEmail,
      userName: person.userName,
      plannedStartDate: dateRange.startDate,
      plannedEndDate: dateRange.endDate,
      actualStartDate: dateRange.actualStartDate,
      actualEndDate: dateRange.actualEndDate,
      skillLevel: person.skillLevel || '초급',
      role: '',
      os: '',
      dbInfo: '',
      language: '',
      assignments: ''
    }));

    setPersonnel(prev => [...prev, ...newPersonnel]);
    handleClosePersonnelModal();
  };

  const handleOpenPersonnelModal = () => setShowPersonnelModal(true);

  const handleClosePersonnelModal = () => {
    setShowPersonnelModal(false);
    setModalStep(1);
    setSearchKeyword('');
    setSelectedPersonnel([]);
    setDateRange({ startDate: '', endDate: '', actualStartDate: '', actualEndDate: '' });
  };

  const handlePersonnelChange = (index, field, value) => {
    const updatedPersonnel = [...personnel];
    const person = { ...updatedPersonnel[index], [field]: value };

    if (field === 'plannedStartDate' || field === 'plannedEndDate') {
      if (!validateDateRange(person.plannedStartDate, person.plannedEndDate, '계획 투입일', '계획 종료일')) return;
    }
    if (field === 'actualStartDate' || field === 'actualEndDate') {
      if (!validateDateRange(person.actualStartDate, person.actualEndDate, '실제 투입일', '실제 종료일')) return;
    }

    updatedPersonnel[index] = person;
    setPersonnel(updatedPersonnel);
  };

  const handleRemovePersonnel = (index) => {
    if (!window.confirm(personnel[index].userName + " 직원을 프로젝트에서 삭제하시겠습니까?")) return;
    setPersonnel(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddExtension = () => {
    setContractExtensions(prev => [...prev, { extensionStartDate: '', extensionEndDate: '' }]);
  };

  const handleExtensionChange = (index, field, value) => {
    if (field === 'extensionStartDate' && value) {
      // 첫 연장은 계약 종료일 이후, 두 번째 이후 연장은 직전 연장 종료일 이후여야 함
      const referenceEndDate = index === 0
        ? projectData.contractualEndDate
        : contractExtensions[index - 1]?.extensionEndDate;
      if (referenceEndDate && value <= referenceEndDate) {
        toast.error(index === 0
          ? '연장 시작일은 계약 종료일 이후로 입력해주세요.'
          : '연장 시작일은 직전 연장 종료일 이후로 입력해주세요.');
        return;
      }
    }

    const updatedExtensions = [...contractExtensions];
    const extension = { ...updatedExtensions[index], [field]: value };
    if (!validateDateRange(extension.extensionStartDate, extension.extensionEndDate, '연장 시작일', '연장 종료일')) return;
    updatedExtensions[index] = extension;
    setContractExtensions(updatedExtensions);
  };

  const handleRemoveExtension = (index) => {
    if (!window.confirm('이 연장 계약을 삭제하시겠습니까?')) return;
    setContractExtensions(prev => prev.filter((_, i) => i !== index));
  };

  const handleOpenTechStack = (index) => {
    setTechStackIndex(index);
    setTechStackForm({
      os: personnel[index].os || '',
      dbInfo: personnel[index].dbInfo || '',
      language: personnel[index].language || '',
    });
    setShowTechStackModal(true);
  };

  const handleSaveTechStack = () => {
    const updated = [...personnel];
    updated[techStackIndex] = { ...updated[techStackIndex], ...techStackForm };
    setPersonnel(updated);
    setShowTechStackModal(false);
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length + attachedFiles.length > 10) {
      toast.error('최대 10개의 파일만 첨부할 수 있습니다.');
      e.target.value = null;
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    const invalidFiles = files.filter(file => file.size > maxSize);
    if (invalidFiles.length > 0) {
      toast.error('파일 크기는 10MB를 초과할 수 없습니다.');
      e.target.value = null;
      return;
    }

    if (isEditMode && projectData.projectId) {
      try {
        const uploadPromises = files.map(async (file) => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('projectId', projectData.projectId);
          formData.append('userEmail', userEmail);
          const response = await compatApi.upload('/file/upload/project', formData);
          if (response.ok) return { ...response.data, file };
          throw new Error(`${file.name} 업로드 실패`);
        });
        const uploadedFiles = await Promise.all(uploadPromises);
        setAttachedFiles(prev => [...prev, ...uploadedFiles]);
        loadImagePreviews(uploadedFiles);
        toast.success(`${files.length}개 파일이 업로드되었습니다.`);
      } catch (error) {
        console.error('파일 업로드 오류:', error);
        toast.error('파일 업로드 중 오류가 발생했습니다.');
      }
    } else {
      const timestamp = Date.now();
      const newFiles = files.map((file, idx) => ({ file, tempId: `temp_${timestamp}_${idx}` }));
      setAttachedFiles(prev => [...prev, ...newFiles]);

      const localPreviews = {};
      files.forEach((file, idx) => {
        if (isImageFile(file.name)) {
          localPreviews[`temp_${timestamp}_${idx}`] = URL.createObjectURL(file);
        }
      });
      setFilePreviewUrls(prev => ({ ...prev, ...localPreviews }));
    }
    e.target.value = null;
  };

  const handleDownloadFile = async (fileId, fileName) => {
    const success = await downloadFile(fileId, fileName);
    if (success) toast.success('파일 다운로드가 완료되었습니다.');
    else toast.error('파일 다운로드에 실패했습니다.');
  };

  const handleRemoveFile = async (index) => {
    const fileObj = attachedFiles[index];
    if (fileObj.id) {
      try {
        const response = await compatApi.post('/file/delete/project', fileObj.id);
        if (response.ok) {
          toast.success('파일이 삭제되었습니다.');
          setAttachedFiles(prev => prev.filter((_, i) => i !== index));
          if (filePreviewUrls[fileObj.id]) {
            revokeImageUrl(filePreviewUrls[fileObj.id]);
            setFilePreviewUrls(prev => { const newUrls = { ...prev }; delete newUrls[fileObj.id]; return newUrls; });
          }
        } else {
          toast.error('파일 삭제에 실패했습니다.');
        }
      } catch (error) {
        console.error('파일 삭제 오류:', error);
        toast.error('파일 삭제 중 오류가 발생했습니다.');
      }
    } else {
      const tempId = fileObj.tempId;
      setAttachedFiles(prev => prev.filter((_, i) => i !== index));
      if (tempId && filePreviewUrls[tempId]) {
        revokeImageUrl(filePreviewUrls[tempId]);
        setFilePreviewUrls(prev => { const newUrls = { ...prev }; delete newUrls[tempId]; return newUrls; });
      }
    }
  };

  const handleSave = async () => {
    if (!isMaster) { toast.error('MASTER 권한이 필요합니다.'); return; }
    try {
      if (!projectData.projectName.trim()) { toast.error('프로젝트 명을 입력해주세요.'); return; }
      if (!projectData.contractualStartDate || !projectData.contractualEndDate) { toast.error('계약시작일과 계약종료일을 입력해주세요.'); return; }
      if (contractExtensions.some(e => !e.extensionStartDate || !e.extensionEndDate)) {
        toast.error('계약 연장의 시작일과 종료일을 모두 입력해주세요.');
        return;
      }

      const requestData = {
        ...projectData,
        organizationCode: currentOrganizationCode,
        personnelList: personnel.map(p => ({
          ...p,
          startDate: p.plannedStartDate,
          endDate: p.plannedEndDate,
          actualStartDate: p.actualStartDate,
          actualEndDate: p.actualEndDate,
        })),
        contractExtensions,
      };

      const endpoint = isEditMode ? '/project/update' : '/project/create';
      const response = await compatApi.post(endpoint, requestData);

      if (response.ok) {
        const savedProjectId = response.data?.projectId || projectData.projectId;
        if (!isEditMode && attachedFiles.length > 0 && savedProjectId) {
          try {
            const uploadPromises = attachedFiles.map(async (fileObj) => {
              const formData = new FormData();
              formData.append('file', fileObj.file);
              formData.append('projectId', savedProjectId);
              formData.append('userEmail', userEmail);
              return await compatApi.post('/file/upload/project', formData);
            });
            await Promise.all(uploadPromises);
            toast.success('프로젝트와 파일이 저장되었습니다.');
          } catch (error) {
            console.error('파일 업로드 오류:', error);
            toast.warning('프로젝트는 저장되었으나 일부 파일 업로드에 실패했습니다.');
          }
        } else {
          toast.success(isEditMode ? '프로젝트가 수정되었습니다.' : '프로젝트가 생성되었습니다.');
          if (!isEditMode) navigate('/project/list');
          else await loadProjectData(projectData.projectId);
        }
      } else {
        toast.error(isEditMode ? '프로젝트 수정에 실패했습니다.' : '프로젝트 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('프로젝트 저장 오류:', error);
      toast.error('프로젝트 저장 중 오류가 발생했습니다.');
    }
  };

  const handleCancel = () => {
    if (window.confirm('변경사항이 저장되지 않습니다. 목록으로 돌아가시겠습니까?')) {
      navigate('/project/list');
    }
  };

  const handleDelete = async () => {
    if (!isMaster) { toast.error('MASTER 권한이 필요합니다.'); return; }
    if (!isEditMode || !projectData.projectId) { toast.error('삭제할 프로젝트가 없습니다.'); return; }
    if (!window.confirm('정말 이 프로젝트를 삭제하시겠습니까?')) return;
    try {
      const response = await compatApi.post('/project/delete/' + projectData.projectId, { projectId: projectData.projectName });
      if (response.ok) {
        toast.success(projectData.projectName + ' 프로젝트가 삭제되었습니다.');
        navigate('/project/list');
      } else {
        toast.error('프로젝트 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('프로젝트 삭제 오류:', error);
      toast.error('프로젝트 삭제 중 오류가 발생했습니다.');
    }
  };

  // --- Design System Helpers ---
  const cardS = {
    background: 'white', borderRadius: 16,
    border: '1px solid #f0eff5',
    boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
    overflow: 'hidden',
  };

  const sHead = (title, action) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: 'linear-gradient(180deg,#faf9fd 0%,#f5f4fa 100%)', borderBottom: '1px solid #ececf2' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ width: 4, height: 16, borderRadius: 2, background: `linear-gradient(180deg,${PURPLE},${MINT})`, flexShrink: 0, display: 'inline-block' }} />
        <span style={{ fontSize: 14.5, fontWeight: 600, color: '#1f1d2e' }}>{title}</span>
      </div>
      {action}
    </div>
  );

  const inp = {
    height: 34, borderRadius: 6, border: '1.5px solid #ececf2',
    background: '#fafaff', padding: '0 10px', fontSize: 13,
    color: '#1f1d2e', outline: 'none', fontFamily: 'inherit',
    width: '100%', boxSizing: 'border-box'
  };

  const b = (variant, isSmall) => {
    const h = isSmall ? 28 : 38;
    const p = isSmall ? '0 10px' : '0 20px';
    const base = { height: h, padding: p, borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: isSmall ? 12 : 14, fontWeight: 600, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, whiteSpace: 'nowrap', transition: 'all 0.2s' };
    if (variant === 'primary') return { ...base, background: PURPLE, color: 'white' };
    if (variant === 'danger') return { ...base, background: '#ef4444', color: 'white' };
    if (variant === 'outline-secondary') return { ...base, background: 'white', color: '#6b7280', border: '1px solid #d1d5db' };
    if (variant === 'outline-primary') return { ...base, background: 'white', color: PURPLE, border: `1px solid ${hexToRgba(PURPLE, .4)}` };
    if (variant === 'success') return { ...base, background: MINT, color: 'white' };
    return { ...base, background: '#e5e7eb', color: '#374151' }; // secondary
  };

  const tblS = { width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13 };
  const thS = { padding: '11px 14px', fontSize: 12.5, fontWeight: 600, color: '#5d5b6e', background: '#f7f6fb', borderBottom: '1px solid #ececf2' };
  const tdS = { padding: '12px 14px', borderBottom: '1px solid #f4f3f9', verticalAlign: 'middle' };

  return (
    <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)', paddingBottom: 80 }}>
      <div style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>

        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 12, color: '#8b8a99' }}>
            <span>프로젝트 관리</span>
            <span style={{ fontSize: 10 }}>›</span>
            <span style={{ color: '#3a3849', fontWeight: 500 }}>{isEditMode ? '프로젝트 수정' : '새 프로젝트'}</span>
          </div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>
            {isEditMode ? '프로젝트 수정' : '새 프로젝트 생성'}
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13.5, color: '#7a7989' }}>프로젝트의 기본 정보, 인원, 문서 등을 관리합니다.</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* 1. 프로젝트 기본 정보 */}
          <div style={cardS}>
            {sHead('프로젝트 기본 정보')}
            <div style={{ padding: '24px 20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 40, rowGap: 16 }}>
                <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>프로젝트 명 <span style={{ color: '#ef4444' }}>*</span></div>
                  <input style={inp} type="text" name="projectName" value={projectData.projectName} onChange={handleProjectInfoChange} disabled={!isMaster} placeholder="프로젝트 명을 입력하세요 (최대 100자)" maxLength={100} />
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>구분 <span style={{ color: '#ef4444' }}>*</span></div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[
                      { value: 'GENERAL', label: '일반' },
                      { value: 'MAINTENANCE', label: '유지보수' },
                    ].map(opt => {
                      const active = projectData.projectType === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          disabled={!isMaster}
                          onClick={() => setProjectData(prev => ({ ...prev, projectType: opt.value }))}
                          style={{
                            height: 34, padding: '0 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: isMaster ? 'pointer' : 'default', fontFamily: 'inherit', transition: 'all .15s',
                            background: active ? `linear-gradient(135deg, ${PURPLE}, ${MINT})` : '#f4f3fa',
                            color: active ? '#fff' : '#7a7989',
                            border: active ? 'none' : '1.5px solid #e2e0f0',
                            boxShadow: active ? `0 4px 10px -4px ${hexToRgba(PURPLE, 0.45)}` : 'none',
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>발주처</div>
                  <input style={inp} type="text" name="client" value={projectData.client} onChange={handleProjectInfoChange} disabled={!isMaster} placeholder="고객사를 입력하세요 (최대 100자)" maxLength={100} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>계약처</div>
                  <input style={inp} type="text" name="contract" value={projectData.contract} onChange={handleProjectInfoChange} disabled={!isMaster} placeholder="고객사를 입력하세요" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>계약 시작일 <span style={{ color: '#ef4444' }}>*</span></div>
                  <input style={inp} type="date" name="contractualStartDate" value={projectData.contractualStartDate} onChange={handleProjectInfoChange} disabled={!isMaster} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>계약 종료일 <span style={{ color: '#ef4444' }}>*</span></div>
                  <input style={inp} type="date" name="contractualEndDate" value={projectData.contractualEndDate} onChange={handleProjectInfoChange} disabled={!isMaster} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>실제 시작일</div>
                  <input style={inp} type="date" name="startDate" value={projectData.startDate} onChange={handleProjectInfoChange} disabled={!isMaster} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>실제 종료일</div>
                  <input style={inp} type="date" name="endDate" value={projectData.endDate} onChange={handleProjectInfoChange} disabled={!isMaster} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>계약일</div>
                  <input style={inp} type="date" name="contractedDate" value={projectData.contractedDate} onChange={handleProjectInfoChange} disabled={!isMaster} />
                </div>
                <div></div> {/* 빈 칸 채우기 */}

                {/* 계약 연장 */}
                <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'start' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e', paddingTop: 6 }}>계약 연장</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {contractExtensions.map((extension, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '46%', minWidth: 280 }}>
                        <input style={{ ...inp, flex: 1, minWidth: 0 }} type="date" value={extension.extensionStartDate} onChange={(e) => handleExtensionChange(index, 'extensionStartDate', e.target.value)} disabled={!isMaster} />
                        <span style={{ color: '#8b8a99' }}>~</span>
                        <input style={{ ...inp, flex: 1, minWidth: 0 }} type="date" value={extension.extensionEndDate} onChange={(e) => handleExtensionChange(index, 'extensionEndDate', e.target.value)} disabled={!isMaster} />
                        {isMaster && (
                          <button type="button" onClick={() => handleRemoveExtension(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0 }} title="연장 삭제">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                          </button>
                        )}
                      </div>
                    ))}
                    {contractExtensions.length === 0 && (
                      <div style={{ fontSize: 12.5, color: '#aaa9b7' }}>등록된 연장 계약이 없습니다.</div>
                    )}
                    {isMaster && (
                      <button type="button" onClick={handleAddExtension} style={{ ...b('outline-primary', true), alignSelf: 'flex-start' }}>+ 연장 추가</button>
                    )}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>주소</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input style={{ ...inp, flex: 1 }} type="text" name="address" value={projectData.address} onChange={handleProjectInfoChange} disabled={!isMaster} placeholder="주소를 입력하세요" />
                    <button style={b('outline-primary', true)} onClick={handleSearchAddress} disabled={!isMaster}>주소 검색</button>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>상세주소</div>
                  <input style={inp} type="text" name="addressDetail" value={projectData.addressDetail} onChange={handleProjectInfoChange} disabled={!isMaster} placeholder="상세주소를 입력하세요" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>담당자</div>
                  <input style={inp} type="text" name="manager" value={projectData.manager} onChange={handleProjectInfoChange} disabled={!isMaster} placeholder="담당자 이름을 입력하세요" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>담당자 연락처</div>
                  <input style={inp} type="text" name="managerPhone" value={projectData.managerPhone} onChange={handleProjectInfoChange} disabled={!isMaster} placeholder="연락처를 입력하세요" />
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>설명</div>
                  <div>
                    <input style={inp} type="text" name="content" value={projectData.content} onChange={handleProjectInfoChange} disabled={!isMaster} placeholder="비고를 입력하세요 (최대 100자)" maxLength={100} />
                    <div style={{ fontSize: 11, color: (projectData.content || '').length >= 100 ? '#e5484d' : '#8b8a99', textAlign: 'right', marginTop: 2 }}>{(projectData.content || '').length}/100</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 2. 인원 정보 */}
          <div style={cardS}>
            {sHead('인원 정보', isMaster ? <button onClick={handleOpenPersonnelModal} style={b('outline-primary', true)}>+ 인원 추가</button> : null)}
            <div style={{ overflowX: 'auto' }}>
              <table style={tblS}>
                <thead>
                  <tr>
                    <th style={{ ...thS, textAlign: 'center', width: '5%' }}>번호</th>
                    <th style={{ ...thS, width: '10%' }}>이름</th>
                    <th style={{ ...thS, textAlign: 'center', width: '20%' }}>계획기간</th>
                    <th style={{ ...thS, textAlign: 'center', width: '20%' }}>실제기간</th>
                    <th style={{ ...thS, width: '10%' }}>등급</th>
                    <th style={{ ...thS, width: '10%' }}>역할</th>
                    <th style={{ ...thS, width: '15%' }}>담당업무</th>
                    <th style={{ ...thS, textAlign: 'center', width: '5%' }}>설정</th>
                    <th style={{ ...thS, textAlign: 'center', width: '5%' }}>삭제</th>
                  </tr>
                </thead>
                <tbody>
                  {personnel.length > 0 ? (
                    personnel.map((person, index) => (
                      <tr key={index}>
                        <td style={{ ...tdS, textAlign: 'center', color: '#8b8a99' }}>{index + 1}</td>
                        <td style={{ ...tdS, fontWeight: 500 }}>{person.userName}</td>
                        <td style={tdS}>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <input type="date" style={{ ...inp, padding: '0 4px', fontSize: 12 }} value={person.plannedStartDate || ''} onChange={(e) => handlePersonnelChange(index, 'plannedStartDate', e.target.value)} disabled={!isMaster} />
                            <span>~</span>
                            <input type="date" style={{ ...inp, padding: '0 4px', fontSize: 12 }} value={person.plannedEndDate || ''} onChange={(e) => handlePersonnelChange(index, 'plannedEndDate', e.target.value)} disabled={!isMaster} />
                          </div>
                        </td>
                        <td style={tdS}>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <input type="date" style={{ ...inp, padding: '0 4px', fontSize: 12 }} value={person.actualStartDate || ''} onChange={(e) => handlePersonnelChange(index, 'actualStartDate', e.target.value)} disabled={!isMaster} />
                            <span>~</span>
                            <input type="date" style={{ ...inp, padding: '0 4px', fontSize: 12 }} value={person.actualEndDate || ''} onChange={(e) => handlePersonnelChange(index, 'actualEndDate', e.target.value)} disabled={!isMaster} />
                          </div>
                        </td>
                        <td style={tdS}>
                          <select style={inp} value={person.skillLevel} onChange={(e) => handlePersonnelChange(index, 'skillLevel', e.target.value)} disabled={!isMaster}>
                            <option value="초급">초급</option>
                            <option value="중급">중급</option>
                            <option value="고급">고급</option>
                            <option value="특급">특급</option>
                          </select>
                        </td>
                        <td style={tdS}>
                          <input type="text" style={inp} value={person.role || ''} onChange={(e) => handlePersonnelChange(index, 'role', e.target.value)} disabled={!isMaster} placeholder="역할" />
                        </td>
                        <td style={tdS}>
                          <input type="text" style={inp} value={person.assignments} onChange={(e) => handlePersonnelChange(index, 'assignments', e.target.value)} disabled={!isMaster} placeholder="담당업무" />
                        </td>
                        <td style={{ ...tdS, textAlign: 'center' }}>
                          <button onClick={() => handleOpenTechStack(index)} disabled={!isMaster} style={{ background: 'none', border: 'none', cursor: isMaster ? 'pointer' : 'default', padding: 0 }} title="기술스택 설정">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={PURPLE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>
                          </button>
                        </td>
                        <td style={{ ...tdS, textAlign: 'center' }}>
                          <button onClick={() => handleRemovePersonnel(index)} disabled={!isMaster} style={{ background: 'none', border: 'none', cursor: isMaster ? 'pointer' : 'default', padding: 0 }} title="삭제">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="9" style={{ padding: '30px', textAlign: 'center', color: '#aaa9b7' }}>인원을 추가해주세요.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* 3. 프로젝트 메모 및 파일 */}
          <div style={cardS}>
            {sHead('프로젝트 메모 및 문서')}
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e', marginBottom: 8 }}>파일 첨부</div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <label htmlFor="file-upload" style={b('outline-primary', true)}>
                    + 파일 추가
                  </label>
                  <input id="file-upload" type="file" multiple hidden onChange={handleFileChange} disabled={!isMaster} />
                  <span style={{ fontSize: 12, color: '#8b8a99' }}>* 최대 10개, 각 10MB 이하</span>
                </div>

                {attachedFiles.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12, marginTop: 16 }}>
                    {attachedFiles.map((fileObj, index) => {
                      const fileName = fileObj.file?.name || fileObj.fileName;
                      const isImage = isImageFile(fileName);
                      const previewUrl = fileObj.id ? filePreviewUrls[fileObj.id] : filePreviewUrls[fileObj.tempId];
                      return (
                        <div key={index} style={{ border: '1px solid #ececf2', borderRadius: 8, overflow: 'hidden', background: 'white' }}>
                          <div style={{ height: 80, background: '#fafaff', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #ececf2' }}>
                            {isImage && isMaster && previewUrl ? (
                              <img src={previewUrl} alt={fileName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
                            ) : (
                              <span style={{ fontSize: 11, color: '#aaa9b7' }}>미리보기 없음</span>
                            )}
                          </div>
                          <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <span style={{ fontSize: 11, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={fileName}>{fileName}</span>
                            <div style={{ display: 'flex', gap: 4 }}>
                              {fileObj.id && isMaster && <button onClick={() => handleDownloadFile(fileObj.id, fileName)} style={{ ...b('outline-secondary', true), flex: 1, padding: '0 4px', fontSize: 10, height: 22 }}>다운</button>}
                              {isMaster && <button onClick={() => handleRemoveFile(index)} style={{ ...b('danger', true), flex: 1, padding: '0 4px', fontSize: 10, height: 22 }}>삭제</button>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e', marginBottom: 8 }}>메모</div>
                <textarea
                  name="memo"
                  value={projectData.memo}
                  onChange={handleProjectInfoChange}
                  disabled={!isMaster}
                  style={{ ...inp, height: 120, padding: 12, resize: 'vertical' }}
                  placeholder="프로젝트와 관련된 참고 사항을 자유롭게 적어주세요."
                />
              </div>
            </div>
          </div>

          {/* 4. 액션 버튼 */}
          {isMaster && (
            <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'rgba(255,255,255,.92)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', borderTop: '1px solid #ececf2', padding: '12px clamp(20px, 3vw, 40px)', display: 'flex', justifyContent: 'flex-end', gap: 8, zIndex: 20 }}>
              <button onClick={() => navigate('/project/list')} style={{ height: 40, padding: '0 16px', borderRadius: 10, background: 'white', border: '1.5px solid #ececf2', color: '#5d5b6e', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'translateX(-2px)' }}><path d="m15 18-6-6 6-6"/></svg>
                목록으로
              </button>
              <button onClick={handleCancel} style={{ height: 40, padding: '0 18px', borderRadius: 10, background: '#f4f3f9', border: 'none', color: '#5d5b6e', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>취소</button>
              {isEditMode && (
                <button onClick={handleDelete} style={{ height: 40, padding: '0 18px', borderRadius: 10, background: 'white', border: '1.5px solid #f0c0c0', color: '#c44569', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>삭제</button>
              )}
              <button onClick={() => navigate('/gantt-chart')} style={{ height: 40, padding: '0 18px', borderRadius: 10, background: 'white', border: `1.5px solid ${MINT}`, color: MINT, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
                간트차트
              </button>
              <button onClick={handleSave} style={{ height: 40, padding: '0 22px', borderRadius: 10, border: 'none', background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', boxShadow: `0 6px 14px -6px ${hexToRgba(PURPLE, 0.55)}`, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m22 2-7 20-4-9-9-4z" /><path d="M22 2 11 13" />
                </svg>
                저장
              </button>
            </div>
          )}

        </div>
      </div>

      {/* 모달: 인원 추가 */}
      {showPersonnelModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 16, width: 600, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(60,50,120,.2)', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #ececf2', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1f1d2e' }}>{modalStep === 1 ? '프로젝트 인원 추가' : '프로젝트 인원 기간 설정'}</h3>
              <button onClick={handleClosePersonnelModal} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#8b8a99', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ padding: 24, overflowY: 'auto' }}>
              {modalStep === 1 ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fafaff', border: '1.5px solid #ececf2', borderRadius: 8, padding: '0 12px', height: 40, marginBottom: 16 }}>
                    <BsSearch color="#8b8a99" />
                    <input type="text" placeholder="이름을 검색하세요" value={searchKeyword} onChange={(e) => setSearchKeyword(e.target.value)} style={{ border: 'none', background: 'transparent', outline: 'none', flex: 1, fontSize: 13 }} />
                  </div>
                  <div style={{ border: '1px solid #ececf2', borderRadius: 8, maxHeight: 300, overflowY: 'auto' }}>
                    {getFilteredWorkmanList().map((person) => (
                      <div key={person.userEmail} onClick={() => handleTogglePersonnel(person)} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #f4f3f9', cursor: 'pointer', background: selectedPersonnel.some(p => p.userEmail === person.userEmail) ? '#f0eeff' : 'white' }}>
                        <input type="checkbox" checked={selectedPersonnel.some(p => p.userEmail === person.userEmail)} readOnly style={{ accentColor: PURPLE, marginRight: 12, cursor: 'pointer' }} />
                        <span style={{ fontSize: 11, fontWeight: 600, background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 6, marginRight: 12 }}>{person.skillLevel || '미지정'}</span>
                        <span style={{ fontSize: 13.5, fontWeight: 500, color: '#1f1d2e' }}>{person.userName} <span style={{ color: '#8b8a99', fontSize: 12 }}>({person.userEmail})</span></span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr auto 1fr', alignItems: 'center', gap: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>계획 기간</div>
                    <input type="date" style={inp} value={dateRange.startDate} onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))} />
                    <span>~</span>
                    <input type="date" style={inp} value={dateRange.endDate} onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr auto 1fr', alignItems: 'center', gap: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#5d5b6e' }}>실제 기간</div>
                    <input type="date" style={inp} value={dateRange.actualStartDate} onChange={(e) => setDateRange(prev => ({ ...prev, actualStartDate: e.target.value }))} />
                    <span>~</span>
                    <input type="date" style={inp} value={dateRange.actualEndDate} onChange={(e) => setDateRange(prev => ({ ...prev, actualEndDate: e.target.value }))} />
                  </div>
                  <div style={{ background: '#fafaff', border: '1px solid #ececf2', borderRadius: 8, padding: 16 }}>
                    <div style={{ fontSize: 12, color: '#8b8a99', marginBottom: 8 }}>선택된 인원</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {selectedPersonnel.map(p => (
                        <span key={p.userEmail} style={{ fontSize: 12, fontWeight: 600, background: '#e5e7eb', color: '#374151', padding: '4px 10px', borderRadius: 6 }}>{p.userName}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid #ececf2', background: '#fafaff', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              {modalStep === 1 ? (
                <>
                  <button style={b('outline-secondary')} onClick={handleClosePersonnelModal}>취소</button>
                  <button style={b('primary')} onClick={handleNextStep} disabled={selectedPersonnel.length === 0}>다음</button>
                </>
              ) : (
                <>
                  <button style={b('outline-secondary')} onClick={handlePreviousStep}>이전</button>
                  <button style={b('primary')} onClick={handleAddPersonnelComplete} disabled={!dateRange.startDate || !dateRange.endDate}>완료</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 모달: 기술스택 설정 */}
      {showTechStackModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 16, width: 400, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(60,50,120,.2)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #ececf2', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1f1d2e' }}>기술스택 설정</h3>
              <button onClick={() => setShowTechStackModal(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#8b8a99', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: 24 }}>
              {techStackIndex !== null && (
                <div style={{ marginBottom: 16, fontSize: 13, color: '#5d5b6e', fontWeight: 600 }}>{personnel[techStackIndex]?.userName}</div>
              )}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: '#5d5b6e', marginBottom: 6 }}>OS</div>
                <input style={inp} type="text" value={techStackForm.os} onChange={(e) => setTechStackForm(prev => ({ ...prev, os: e.target.value }))} placeholder="예: Windows, Linux, macOS" />
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: '#5d5b6e', marginBottom: 6 }}>DB</div>
                <input style={inp} type="text" value={techStackForm.dbInfo} onChange={(e) => setTechStackForm(prev => ({ ...prev, dbInfo: e.target.value }))} placeholder="예: Oracle, MySQL" />
              </div>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: '#5d5b6e', marginBottom: 6 }}>언어</div>
                <input style={inp} type="text" value={techStackForm.language} onChange={(e) => setTechStackForm(prev => ({ ...prev, language: e.target.value }))} placeholder="예: Java, Python" />
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid #ececf2', background: '#fafaff', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={b('outline-secondary')} onClick={() => setShowTechStackModal(false)}>취소</button>
              <button style={b('primary')} onClick={handleSaveTechStack}>저장</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
